import AccountBalanceIcon from "@mui/icons-material/AccountBalance"
import AssessmentIcon from "@mui/icons-material/Assessment"
import CampaignIcon from "@mui/icons-material/Campaign"
import CorporateFareIcon from "@mui/icons-material/CorporateFare"
import EuroIcon from "@mui/icons-material/Euro"
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"
import HomeIcon from "@mui/icons-material/Home"
import MenuIcon from "@mui/icons-material/Menu"
import PeopleAltIcon from "@mui/icons-material/PeopleAlt"
import {
	AppBar,
	CssBaseline,
	Grid,
	IconButton,
	Link as LinkMaterial,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Toolbar,
} from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"
import { FC, ReactElement, ReactNode, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Link, useLocation } from "react-router-dom"

import PaydoLogo from "../../assets/img/powered.png"
import BankAutoComplete from "../../components/filters/bank-autocomplete"
import { AppDispatch } from "../../store"
import { BankEssential } from "../../store/actions/bank"
import {
	CorporateEssential,
	getUserAllCorporates,
} from "../../store/actions/corporates"
import { Permission } from "../../store/actions/user"
import { selectBank } from "../../store/bank/bank-all-slice"
import { selectCorporate } from "../../store/corporates/corporates-all-slice"
import { RootState } from "../../store/reducers"
import { getBestContrastColor } from "../../utils/contrast-color"
import MenuDrawer from "../common/menu-drawer"
import CorporateAutoComplete from "../filters/corporate-autocomplete"
import LanguageSelector from "../langSelector"
import ScrollToTop from "../scroll-to-top"
import UserMenu from "../userMenu"

interface MenuItem {
	text: string
	icon: ReactElement
	path: string | string[]
	permission?: string
}

const PREFIX = "MenuExpandable"
const classes = {
	activeMenuItem: `${PREFIX}-activeMenuItem`,
	menuItem: `${PREFIX}-menuItem`,
	appBar: `${PREFIX}-appBar`,
}

const Root = styled("div")(({ theme }) => ({
	[`& .${classes.activeMenuItem}`]: {
		backgroundColor: theme.palette.secondary.main,
		color: theme.palette.secondary.contrastText,
		transition: "background-color 0.3s ease",
		boxShadow: "0 2px 4px rgba(0,0,0, 0.2)",
	},
	[`& .${classes.menuItem}`]: {
		color: theme.palette.primary.contrastText,
	},
	[`& .${classes.menuItem}:hover`]: {
		backgroundColor: theme.palette.secondary.main,
		color: theme.palette.secondary.contrastText,
		transition: "background-color 0.3s ease",
		boxShadow: "0 2px 4px rgba(0,0,0, 0.2)",
	},
}))

const LoggedStructure: FC<{ children: ReactNode; isNeededBank?: boolean }> = ({
	children,
	isNeededBank,
}) => {
	const { t } = useTranslation()
	const dispatch = useDispatch<AppDispatch>()
	const authState = useSelector((state: RootState) => state.auth)
	const bankListState: BankEssential[] = useSelector(
		(state: RootState) => state.banksUser.list
	)
	const bankSelected: BankEssential | undefined = useSelector(
		(state: RootState) => state.banksUser.selected
	)
	const corporateListState: CorporateEssential[] = useSelector(
		(state: RootState) => state.corporatesUser.list
	)
	const corporateSelected: CorporateEssential | undefined = useSelector(
		(state: RootState) => state.corporatesUser.selected
	)
	const [filterCorporate, setFilterCorporate] = useState<undefined | string>(
		undefined
	)
	const [open, setOpen] = useState<boolean>(false)
	const [isAutocompleteBankList, setIsAutocompleteBankList] =
		useState<boolean>(false)
	const [isAutocompleteCorporateList, setIsAutocompleteCorporateList] =
		useState<boolean>(false)
	const [imgBankSrc, setImgBankSrc] = useState<string | undefined>(undefined)
	const [imgCorporateSrc, setImgCorporateSrc] = useState<string | undefined>(
		undefined
	)
	const location = useLocation()
	const theme = useTheme()

	const handleToggle = () => {
		setOpen(!open)
	}

	const menuItems: MenuItem[] = [
		{ text: t("menu.home", { ns: "userMenu" }), icon: <HomeIcon />, path: "/" },
		{
			text: t("menu.user", { ns: "userMenu" }),
			icon: <PeopleAltIcon />,
			path: "/users",
			permission: "CRUD_USER",
		},
		{
			text: t("menu.file", { ns: "userMenu" }),
			icon: <EuroIcon />,
			path: ["/distinte", "/distinta", "/upload"],
			permission: "VIEW_FILE",
		},
		{
			text: t("menu.banks", { ns: "userMenu" }),
			icon: <AccountBalanceIcon />,
			path: "/banks",
			permission: "CRUD_BANK",
		},
		{
			text: t("menu.corporate", { ns: "userMenu" }),
			icon: <CorporateFareIcon />,
			path: "/corporate",
			permission: "CRUD_CORPORATE",
		},
		{
			text: t("menu.campaigns", { ns: "userMenu" }),
			icon: <CampaignIcon />,
			path: "/campaigns",
			permission: "CRUD_CAMPAIGN",
		},
		{
			text: t("menu.report", { ns: "userMenu" }),
			icon: <AssessmentIcon />,
			path: "/report",
			permission: "REPORT",
		},
		{
			text: t("menu.faq", { ns: "userMenu" }),
			icon: <HelpOutlineIcon />,
			path: "/faq",
		},
	]

	useEffect(() => {
		if (bankSelected?.id) {
			/* Retrieve corporates list for bank selected */
			dispatch(
				getUserAllCorporates({
					page: 0,
					pageSize: 50,
					filter: { bankId: bankSelected.id },
					sort: "corporateSenderName:asc",
				})
			)
				.then(() => {})
				.catch((e) => {})
			/** Retrieve bank image */
			const base64Image = bankSelected?.img ?? undefined
			if (base64Image) setImgBankSrc(atob(base64Image /*.split('/')[1]*/))
		}
	}, [bankSelected?.id, bankSelected?.img])

	useEffect(() => {
		if (corporateSelected) {
			/** Retrieve corporate image */
			const base64Image = corporateSelected?.img ?? undefined
			if (base64Image) setImgCorporateSrc(atob(base64Image))
		}
	}, [corporateSelected])

	useEffect(() => {
		const infoBank = bankListState?.find(
			(bank: BankEssential) => bank.id === authState.user?.idBank
		)
		const selectedBank: BankEssential = infoBank || bankListState?.[0]
		dispatch(selectBank(selectedBank))
	}, [bankListState, authState.user?.idBank])

	useEffect(() => {
		const infoCorporate = corporateListState?.find(
			(corporate: CorporateEssential) =>
				corporate.id === authState.user?.corporateSenderId
		)
		const selectedCorporate = infoCorporate
		if (selectedCorporate) dispatch(selectCorporate(selectedCorporate))
	}, [corporateListState, authState.user?.corporateSenderId])

	useEffect(() => {
		if (filterCorporate) {
			const infoCorporate = corporateListState?.find(
				(corporate: CorporateEssential) => corporate.id === filterCorporate
			)
			dispatch(selectCorporate(infoCorporate))
		}
	}, [filterCorporate])

	return (
		<Root
			theme={theme}
			style={{
				display: "flex",
				"--primary-color": theme.palette.primary.main,
				"--secondary-color": theme.palette.secondary.main,
			}}
		>
			<ScrollToTop />
			<CssBaseline />
			<AppBar
				position="fixed"
				style={{
					backgroundColor: theme.palette.primary.contrastText,
					color: theme.palette.primary.main,
				}}
			>
				<Toolbar sx={{ marginLeft: open ? "200px" : "55px" }}>
					<Grid container alignItems={"space-beetween"}>
						{isNeededBank && bankSelected ? (
							<Grid item flexGrow={1}>
								<Grid container alignItems={"center"} gap="16px">
									{authState?.user?.idBank &&
										imgBankSrc &&
										!isAutocompleteBankList && (
											<Grid>
												<img
													onClick={(e) => setIsAutocompleteBankList(true)}
													alt={t("logo.bank", { ns: "common" })}
													style={{
														height: "40px",
														verticalAlign: "middle",
														backgroundColor: "#FFF",
													}}
													src={imgBankSrc}
												/>
											</Grid>
										)}
									{isAutocompleteBankList && (
										<Grid onBlur={(e) => setIsAutocompleteBankList(false)}>
											<BankAutoComplete disableClearable={false} />
										</Grid>
									)}
									{authState?.user?.corporateSenderId &&
										imgCorporateSrc &&
										!isAutocompleteCorporateList && (
											<Grid>
												<img
													onClick={(e) => setIsAutocompleteCorporateList(true)}
													alt={t("logo.corporate", { ns: "common" })}
													style={{ height: "40px", verticalAlign: "middle" }}
													src={imgCorporateSrc}
												/>
											</Grid>
										)}
									{isAutocompleteCorporateList && (
										<Grid onBlur={(e) => setIsAutocompleteCorporateList(false)}>
											<CorporateAutoComplete
												filterCorporate={filterCorporate}
												setFilterCorporate={setFilterCorporate}
											/>
										</Grid>
									)}
								</Grid>
							</Grid>
						) : (
							<Grid item flexGrow={1}>
								{" "}
							</Grid>
						)}
						<Grid item sx={{ alignSelf: "right", textAlign: "right" }}>
							<Grid container alignItems={"center"}>
								<Grid
									item
									style={{
										color: getBestContrastColor(
											theme.palette.primary.contrastText
										),
									}}
								>
									<LanguageSelector />
								</Grid>
								<Grid item>
									<UserMenu />
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Toolbar>
			</AppBar>

			<MenuDrawer variant="permanent" open={open}>
				<List>
					<ListItem
						sx={{
							marginTop: "-15px",
							flexWrap: "wrap",
							justifyContent: open ? "left" : "center",
						}}
					>
						<IconButton
							color="inherit"
							aria-label="open menu"
							edge="start"
							style={{
								color: theme.palette.primary.contrastText,
								marginLeft: 0,
								minWidth: open ? "56px" : "inherit",
							}}
							onClick={handleToggle}
						>
							<MenuIcon />
						</IconButton>
						{open ? (
							<ListItemText primary={"Menu"} />
						) : (
							<small style={{ marginTop: "-10px", fontSize: "10px" }}>
								MENU
							</small>
						)}
					</ListItem>
					{menuItems.map((item: MenuItem[], index: number) => {
						const isActivePath: boolean =
							typeof item.path === "string"
								? location.pathname === item.path
								: item.path.some((str) => location.pathname.includes(str))
						const userPermissions = authState?.user?.permissions
						const hasMenuItemPermission =
							!item.permission ||
							userPermissions?.find(
								(el: Permission) => el.permissionName === item.permission
							)?.enabled
						return (
							hasMenuItemPermission && (
								<ListItem
									key={`Menu-item-${index}`}
									component={Link}
									to={typeof item.path === "string" ? item.path : item.path[0]}
									className={
										isActivePath ? classes.activeMenuItem : classes.menuItem
									}
									sx={{
										flexWrap: "wrap",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<ListItemIcon
										style={{
											color: isActivePath
												? theme.palette.secondary.contrastText
												: theme.palette.primary.contrastText,
											justifyContent: "center",
										}}
									>
										{item.icon}
									</ListItemIcon>
									{open && <ListItemText primary={item.text} />}
								</ListItem>
							)
						)
					})}
				</List>

				<div
					style={{
						fontSize: "70%",
						display: "flex",
						alignItems: "flex-end",
						height: "100%",
						justifyContent: "center",
					}}
				>
					<LinkMaterial
						href="https://plick.eu/"
						target="_blank"
						rel="noopener"
						underline="none"
						color="inherit"
						aria-label={t("accessibilityLabel", { ns: "copyright" })}
						sx={{ textAlign: "center" }}
					>
						<img
							src={PaydoLogo}
							alt=""
							style={{
								width: "80%",
								filter: "brightness(0) invert(1)",
								maxWidth: "80px",
							}}
						/>
					</LinkMaterial>
				</div>
			</MenuDrawer>
			<main
				style={{
					marginTop: "64px",
					padding: "16px",
					width: "100%",
					minHeight: "calc(100vh - 64px)",
					backgroundColor: "#f5f8fa",
				}}
			>
				{!!bankSelected && children}
			</main>
		</Root>
	)
}

export default LoggedStructure
